import React, { useEffect, useState } from 'react';
import {
  Box, AppBar, Toolbar, Typography, IconButton, Button, Container, Paper, Stack, TextField,
  MenuItem, Chip, CircularProgress, Snackbar, Alert, Divider, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Api, TenantView, ContentType, EntryInput } from './api';

const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
const empty: EntryInput = {
  slug: '', title: '', excerpt: '', bodyMarkdown: '', featureImageUrl: '',
  minuteRead: '', externalLink: '', publishedAt: '', status: 'DRAFT',
};

/** Friendly, focused editor for a single collection entry (Insight / News /
 *  Solution Brief). Non-technical: plain labelled fields + an image preview,
 *  no JSON. Create (id === null) or edit. */
export function EntryEditor({
  api, tenant, type, id, canDelete, onBack,
}: {
  api: Api; tenant: TenantView; type: ContentType; id: string | null;
  canDelete: boolean; onBack: () => void;
}) {
  const [form, setForm] = useState<EntryInput | null>(id ? null : { ...empty });
  const [slugEdited, setSlugEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [entryId, setEntryId] = useState<string | null>(id);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);

  useEffect(() => {
    if (!id) return;
    api.entry(tenant.tenantId, id)
      .then((e) => setForm({
        slug: e.slug, title: e.title, excerpt: e.excerpt ?? '', bodyMarkdown: e.bodyMarkdown ?? '',
        featureImageUrl: e.featureImageUrl ?? '', minuteRead: e.minuteRead ?? '',
        externalLink: e.externalLink ?? '', publishedAt: e.publishedAt ? e.publishedAt.slice(0, 10) : '',
        status: e.status,
      }))
      .catch((err) => setToast({ msg: err.message, sev: 'error' }));
  }, [id, tenant.tenantId]);

  const set = (k: keyof EntryInput, v: string) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));

  const onTitle = (v: string) =>
    setForm((f) => (f ? { ...f, title: v, slug: slugEdited || entryId ? f.slug : slugify(v) } : f));

  const save = async (nextStatus?: string) => {
    if (!form) return;
    const payload: EntryInput = { ...form };
    if (!payload.title.trim()) { setToast({ msg: 'Add a title first.', sev: 'error' }); return; }
    if (!payload.slug.trim()) { setToast({ msg: 'Add a URL slug first.', sev: 'error' }); return; }
    setBusy(true);
    try {
      // Editorial fields save first; the live status changes ONLY through the
      // publish/unpublish gate (PUBLISH_ROLES), never via the entry body. (H2)
      let id = entryId;
      if (id) {
        await api.updateEntry(tenant.tenantId, id, payload);
      } else {
        const res = await api.createEntry(tenant.tenantId, type.collection!, payload);
        id = res.id; setEntryId(id);
      }
      if (nextStatus === 'PUBLISHED') await api.publishEntry(tenant.tenantId, id!);
      else if (nextStatus === 'DRAFT') await api.unpublishEntry(tenant.tenantId, id!);
      const status = nextStatus ?? form.status;
      const publishedAt =
        status === 'PUBLISHED' && !payload.publishedAt ? new Date().toISOString().slice(0, 10) : payload.publishedAt;
      setForm({ ...payload, status, publishedAt });
      setToast({ msg: nextStatus === 'PUBLISHED' ? 'Published' : nextStatus === 'DRAFT' ? 'Unpublished' : 'Saved', sev: 'success' });
    } catch (e: any) {
      setToast({ msg: e.message === 'slug_taken' ? 'That URL is already used in this collection.' : e.message === 'invalid_external_link' ? 'The external link must be an http(s), mailto, tel or relative URL.' : `Couldn’t save: ${e.message}`, sev: 'error' });
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!entryId) return;
    setBusy(true);
    try { await api.deleteEntry(tenant.tenantId, entryId); onBack(); }
    catch (e: any) { setToast({ msg: e.message, sev: 'error' }); setBusy(false); }
  };

  if (!form) return <Box sx={{ display: 'grid', placeItems: 'center', height: '100vh' }}><CircularProgress /></Box>;

  const published = form.status === 'PUBLISHED';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar>
          <IconButton edge="start" onClick={onBack}><ArrowBackIcon /></IconButton>
          <Typography variant="h6" sx={{ fontWeight: 700, mr: 1 }}>
            {entryId ? form.title || 'Untitled' : `New ${type.singular}`}
          </Typography>
          <Chip size="small" label={published ? 'Published' : 'Draft'} color={published ? 'success' : 'default'} />
          <Box sx={{ flexGrow: 1 }} />
          {canDelete && entryId ? (
            <Tooltip title="Delete">
              <span><IconButton color="error" onClick={remove} disabled={busy}><DeleteOutlineIcon /></IconButton></span>
            </Tooltip>
          ) : null}
          <Button onClick={() => save('DRAFT')} disabled={busy} sx={{ mr: 1 }}>
            {published ? 'Unpublish' : 'Save draft'}
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={() => save(published ? undefined : 'PUBLISHED')} disabled={busy}>
            {published ? 'Save' : 'Publish'}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Stack spacing={2.5}>
            <TextField label="Title" value={form.title} onChange={(e) => onTitle(e.target.value)} fullWidth autoFocus />
            <TextField
              label="URL slug" value={form.slug}
              onChange={(e) => { setSlugEdited(true); set('slug', slugify(e.target.value)); }}
              fullWidth helperText={`Public URL: /${type.collection === 'insight' ? 'insights' : type.collection === 'new' ? 'news' : 'solution-brief'}/${form.slug || '…'}`}
            />
            <TextField label="Excerpt" value={form.excerpt ?? ''} onChange={(e) => set('excerpt', e.target.value)} fullWidth multiline minRows={2} helperText="Short summary shown on listing cards." />

            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Feature image</Typography></Divider>
            <TextField label="Feature image URL" value={form.featureImageUrl ?? ''} onChange={(e) => set('featureImageUrl', e.target.value)} fullWidth />
            {form.featureImageUrl ? (
              <Box component="img" src={form.featureImageUrl} alt="" sx={{ maxHeight: 180, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
            ) : null}

            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Details</Typography></Divider>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              {/* Status is controlled by the Publish/Unpublish buttons (the publish
                  gate); publishedAt is stamped server-side on publish. (H2) */}
              <TextField label="Published date" type="date" value={form.publishedAt ?? ''} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} helperText="Set automatically on publish." />
              <TextField label="Read time" value={form.minuteRead ?? ''} onChange={(e) => set('minuteRead', e.target.value)} placeholder="5 min read" sx={{ flex: 1 }} />
            </Stack>
            <TextField label="External link (optional)" value={form.externalLink ?? ''} onChange={(e) => set('externalLink', e.target.value)} fullWidth helperText="If set, the listing links straight here instead of a detail page." />

            <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Body (Markdown)</Typography></Divider>
            <TextField label="Body" value={form.bodyMarkdown ?? ''} onChange={(e) => set('bodyMarkdown', e.target.value)} fullWidth multiline minRows={10} placeholder="Write the article in Markdown…" sx={{ '& textarea': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 13 } }} />
          </Stack>
        </Paper>
      </Container>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
